/**
 * ADR-118 — boot-time reconciliation.
 *
 * On every `<AuthProvider>` mount, the lib reads both `localStorage`
 * AND `document.cookie` and decides what the auth state should be.
 *
 *   1. token + user + cookie → hydrate from storage. The "happy
 *      path" — normal returning user, everything present locally.
 *
 *   2. We hold *a* credential to validate — a stored token and/or a
 *      readable cookie — but not the full happy-path triple. Fire a
 *      `/me/` probe and authenticate from the result, or transition
 *      to `logged-out` on 401. This single branch covers:
 *        - cookie present, storage empty (the cookie-only purgatory);
 *        - a valid stored token without a *readable* cookie (the
 *          `jwt-auth` cookie is host-only, so it's invisible to
 *          `document.cookie` whenever the app is served from a host
 *          other than the API: local split-origin and dev);
 *        - a stored token whose user blob was never migrated into
 *          `StorageKeys.user` (a legacy session — the probe
 *          rehydrates the user from `/me/`).
 *      The provider sends the stored token as the Bearer credential
 *      (falling back to the cookie), so a still-valid token survives
 *      a reload and a revoked one 401s the probe (server-side
 *      blocklist).
 *
 *   3. No token AND no cookie AND no refresh token → if the login
 *      adapter can trade the parent-domain HttpOnly SSO assertion for
 *      a session (`ssoBootstrapAvailable`), probe anyway:
 *      the `aegix-sso` assertion is HttpOnly, so it is invisible to
 *      `document.cookie` and "nothing readable locally" cannot rule
 *      out a live cross-app session. Otherwise `anonymous` —
 *      first-time visitor or fully logged-out state.
 *
 * Why not "token-without-cookie → expired"? The token is the bearer
 * credential and demonstrably works in-session; throwing it away on
 * reload because a host-only cookie isn't JS-visible would log every
 * user out on refresh. Revocation is enforced by the probe
 * (and the lazy 401 path), not by cookie presence.
 */

import type { AuthState, BaseUser } from './types';
import { StorageKeys, type StorageAdapter } from './storage';
import { clearAuthCookie } from './cookie';

/**
 * Result of `reconcile()`. The state field is what the provider
 * should `hydrate` to. If `requiresProbe` is true, the provider
 * must additionally call the supplied probe function and dispatch
 * the result before any UI is shown — there's no useful local
 * state to render yet.
 */
export interface ReconcileResult<U extends BaseUser> {
  state: AuthState<U>;
  /**
   * Branch 2, and Branch 3 when `ssoBootstrapAvailable` is set —
   * caller should fire the async boot sequence (boot-refresh / SSO
   * bootstrap / `/me/` probe, provided by the adapter layer via
   * `auth-identity`). On success, dispatch `login_success`; on
   * failure, dispatch `logout` (non-destructively).
   */
  requiresProbe: boolean;
  /**
   * True when we hold at least one locally-detectable
   * credential: a stored access token, a readable cookie, or a stored
   * refresh token (Branches 1 and 2). False on the Branch-3
   * bootstrap-only path: the probe there is speculative (the HttpOnly
   * SSO assertion may or may not exist), so when the bootstrap fails
   * the provider should go straight to `logged-out` rather than fire
   * further fallback round-trips.
   */
  hasLocalCredential: boolean;
}

export interface ReconcileOptions {
  /**
   * The cookie name to check. Defaults to `'jwt-auth'`. Apps using
   * a non-standard cookie name override here.
   */
  cookieName?: string;
  /**
   * Document accessor for testing. Defaults to the global
   * `document`.
   */
  documentRef?: Pick<Document, 'cookie'>;
  /**
   * The login adapter can trade the HttpOnly parent-domain
   * SSO assertion (`aegix-sso`) for a session via
   * `bootstrapFromSso()`. HttpOnly means the assertion's presence
   * cannot be detected client-side — it never appears in
   * `document.cookie` — so when this is set, a no-local-credential
   * boot (Branch 3) must still probe instead of settling on
   * `anonymous`.
   */
  ssoBootstrapAvailable?: boolean;
}

/**
 * Synchronously decide the auth state from localStorage + cookie.
 *
 * Pure function — no side effects beyond reading the storage
 * adapter and `document.cookie`. The provider drives the rest
 * (probe firing, state hydration, cookie clearing).
 */
export function reconcile<U extends BaseUser>(
  storage: StorageAdapter,
  options: ReconcileOptions = {},
): ReconcileResult<U> {
  const {
    cookieName = 'jwt-auth',
    documentRef = typeof document !== 'undefined' ? document : null,
    ssoBootstrapAvailable = false,
  } = options;

  const token = storage.get<string>(StorageKeys.token);
  const user = storage.get<U>(StorageKeys.user);
  const refreshToken =
    storage.get<string>(StorageKeys.refreshToken) ?? undefined;
  const hasCookie = cookiePresent(cookieName, documentRef);

  // Branch 1: happy path — everything present locally.
  if (token && user && hasCookie) {
    return {
      state: {
        status: 'authenticated',
        user,
        jwt: token,
        refreshToken,
      },
      requiresProbe: false,
      hasLocalCredential: true,
    };
  }

  // Branch 2: we hold a credential to validate — a stored access token,
  // a readable cookie, OR a refresh token — but not the full triple.
  // Probe: the provider sends the access token as the Bearer credential
  // (falling back to the cookie); if there's no usable access token but
  // a refresh token exists, boot-time refresh mints a fresh access token
  // via the refresh adapter before the /me probe instead of logging out.
  // Covers the cookie-only purgatory, the unreadable-host-only-cookie
  // case, legacy sessions whose user blob was never migrated, and the
  // mobile pattern's ephemeral-access-token-expired-but-refresh-present
  // reload.
  if (token || hasCookie || refreshToken) {
    return {
      state: { status: 'authenticating' },
      requiresProbe: true,
      hasLocalCredential: true,
    };
  }

  // Branch 3: no token, no cookie, no refresh token. If the
  // adapter can bootstrap from the parent-domain SSO assertion, we
  // cannot rule a live session out from here — the `aegix-sso` cookie
  // is HttpOnly and therefore invisible to `document.cookie` — so
  // probe anyway. `hasLocalCredential: false` tells the provider this
  // probe is speculative: a failed bootstrap should land on
  // `logged-out` without further fallback round-trips.
  if (ssoBootstrapAvailable) {
    return {
      state: { status: 'authenticating' },
      requiresProbe: true,
      hasLocalCredential: false,
    };
  }

  // Otherwise: clean logged-out / first visit.
  return {
    state: { status: 'anonymous' },
    requiresProbe: false,
    hasLocalCredential: false,
  };
}

/**
 * Called by the provider when Branch 2's probe fails. Clears
 * the cookie (best-effort client-side; server-side `/logout/`
 * endpoint is the authoritative path) and clears any stale
 * localStorage keys the lib owns.
 */
export function failProbe(
  storage: StorageAdapter,
  options: ReconcileOptions = {},
): void {
  const { cookieName = 'jwt-auth' } = options;
  clearAuthCookie({ name: cookieName });
  storage.clearAuthKeys();
}

function cookiePresent(
  name: string,
  documentRef: Pick<Document, 'cookie'> | null,
): boolean {
  if (!documentRef) return false;
  // Cheap-but-correct check: look for "<name>=" anywhere in
  // document.cookie. False positives only if a different cookie's
  // value happened to contain the literal string; vanishingly
  // unlikely for our cookie names.
  return new RegExp(`(^|;\\s*)${escapeRegExp(name)}=`).test(
    documentRef.cookie,
  );
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
